import {
  BarChart3,
  DollarSign,
  FileText,
  GraduationCap,
  TrendingUp,
  Users,
} from 'lucide-react';

import type { ReportCategory } from '~pages/payments/payment-reports/report-config';

interface ReportSectionProps {
  title: string;
  description?: string;
  category: ReportCategory;
  children: React.ReactNode;
}

const sectionIcons: Record<
  ReportCategory,
  React.ComponentType<{ className?: string }>
> = {
  Revenue: TrendingUp,
  Finance: DollarSign,
  Analytics: BarChart3,
  Enrollment: Users,
  Billing: FileText,
  Students: GraduationCap,
};

const sectionColors: Record<ReportCategory, { icon: string; accent: string }> =
  {
    Revenue: {
      icon: 'text-emerald-600 bg-emerald-50',
      accent: 'bg-emerald-500',
    },
    Finance: { icon: 'text-blue-600 bg-blue-50', accent: 'bg-blue-500' },
    Analytics: {
      icon: 'text-purple-600 bg-purple-50',
      accent: 'bg-purple-500',
    },
    Enrollment: { icon: 'text-amber-600 bg-amber-50', accent: 'bg-amber-500' },
    Billing: { icon: 'text-orange-600 bg-orange-50', accent: 'bg-orange-500' },
    Students: { icon: 'text-pink-600 bg-pink-50', accent: 'bg-pink-500' },
  };

export const ReportSection = ({
  title,
  description,
  category,
  children,
}: ReportSectionProps) => {
  const Icon = sectionIcons[category];
  const colors = sectionColors[category];

  return (
    <div className='space-y-4'>
      {/* Section Header - Responsive */}
      <div className='flex flex-col gap-3 border-b border-border pb-3 sm:flex-row sm:items-center'>
        <div className='flex flex-1 items-center gap-3'>
          <div className={`shrink-0 rounded-lg p-2 ${colors.icon}`}>
            <Icon className='size-5' />
          </div>
          <div className='min-w-0'>
            <h2 className='text-lg font-semibold text-foreground'>{title}</h2>
            {description && (
              <p className='line-clamp-2 text-sm text-muted-foreground sm:line-clamp-1'>
                {description}
              </p>
            )}
          </div>
        </div>
        <div
          className={`hidden h-1 w-12 rounded-full sm:block ${colors.accent} opacity-60`}
        />
      </div>

      {/* Reports List */}
      <div className='space-y-3'>{children}</div>
    </div>
  );
};
